import { onScopeDispose, watch, type Ref } from 'vue'

import mark from '@/assets/brand/favicon.svg?raw'
import { useReducedMotion } from '@/composables/useReducedMotion'
import { type FaviconBadge, faviconFrames } from '@/lib/activity/favicon-frames'
import { type Ticker, startTicker } from '@/lib/activity/ticker'
import { resolveTokenColor } from '@/lib/color/token-color'
import type { Activity } from '@/types/activity'

/** Milliseconds each frame of a pulsing badge shows for. */
export const FAVICON_FRAME_MS = 100

/** The token each activity paints its badge in; `idle` shows the plain mark. */
const FAVICON_BADGE_TOKENS: Readonly<Record<Exclude<Activity, 'idle'>, `--${string}`>> = {
  working: '--color-accent-blue',
  attention: '--color-accent-blue',
  done: '--color-accent-green',
  failed: '--color-accent-red',
}

function badgeFor(activity: Exclude<Activity, 'idle'>, reducedMotion: boolean): FaviconBadge {
  const token = FAVICON_BADGE_TOKENS[activity]
  return {
    color: { light: resolveTokenColor(token, 'light'), dark: resolveTokenColor(token, 'dark') },
    pulse: activity === 'working' && !reducedMotion,
  }
}

/** Plays frames on the tab icon, or puts the plain mark back. */
interface FaviconPlayer {
  show(frames: readonly string[]): void
  rest(): void
}

function createFaviconPlayer(link: HTMLLinkElement): FaviconPlayer {
  const restingHref = link.href
  let ticker: Ticker | null = null

  function stop(): void {
    ticker?.stop()
    ticker = null
  }

  return {
    show(frames) {
      stop()
      let index = 0
      link.href = frames[0] ?? restingHref
      if (frames.length < 2) return
      ticker = startTicker(FAVICON_FRAME_MS, () => {
        index = (index + 1) % frames.length
        link.href = frames[index] ?? restingHref
      })
    },
    rest() {
      stop()
      link.href = restingHref
    },
  }
}

/** Shows an activity on the tab icon: a breathing dot while work runs, a still dot for an outcome. */
export function useActivityFavicon(activity: Readonly<Ref<Activity>>): void {
  const link = document.querySelector<HTMLLinkElement>('link[rel="icon"]')
  if (link === null) return
  const player = createFaviconPlayer(link)
  const reducedMotion = useReducedMotion()

  watch(
    [activity, reducedMotion],
    ([current, reduced]) => {
      if (current === 'idle') player.rest()
      else player.show(faviconFrames(mark, badgeFor(current, reduced)))
    },
    { immediate: true },
  )

  onScopeDispose(() => player.rest())
}
